import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  CheckCircle2,
  Target,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Shield,
  Sparkles
} from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RiskAnalysisCard = ({ caseId, compact = false }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    if (caseId) {
      fetchAnalysis();
    }
  }, [caseId]);

  const fetchAnalysis = async () => {
    try {
      const res = await axios.get(`${API}/cases/${caseId}/risk-analysis`);
      if (!res.data.error) {
        setAnalysis(res.data);
      }
    } catch (error) {
      console.error("Error fetching analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card-elevated p-6 animate-pulse">
        <div className="h-4 bg-navy-200 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-navy-100 rounded"></div>
      </div>
    );
  }

  if (!analysis || analysis.error) {
    return null;
  }

  // Handle "no data" state
  const hasData = analysis.has_data !== false && analysis.probability_score > 0;
  
  const scoreColor = !hasData ? "text-navy-400" :
    analysis.probability_score >= 70 ? "text-emerald-600" :
    analysis.probability_score >= 40 ? "text-amber-600" : "text-orange-500";

  const bgColor = !hasData ? "from-navy-50 to-slate-50 border-navy-200" :
    analysis.probability_score >= 70 ? "from-emerald-50 to-green-50 border-emerald-200" :
    analysis.probability_score >= 40 ? "from-amber-50 to-yellow-50 border-amber-200" : 
    "from-orange-50 to-amber-50 border-orange-200";

  const ringColor = !hasData ? "text-navy-300" :
    analysis.probability_score >= 70 ? "text-emerald-500" :
    analysis.probability_score >= 40 ? "text-amber-500" : "text-orange-500";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card-elevated overflow-hidden`}
    >
      {/* Header with Score */}
      <div className={`p-6 bg-gradient-to-br ${bgColor} border-b`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Circular Progress */}
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-navy-200"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 35}`}
                  strokeDashoffset={`${2 * Math.PI * 35 * (1 - analysis.probability_score / 100)}`}
                  className={ringColor}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-2xl font-bold ${scoreColor}`}>
                  {hasData ? `${analysis.probability_score}%` : "-"}
                </span>
              </div>
            </div>
            
            <div>
              <h3 className="font-heading text-xl font-bold text-navy-900">
                Probabilidad de Aprobación
              </h3>
              <p className={`text-sm font-medium ${scoreColor}`}>
                {hasData ? `Estado: ${analysis.risk_label}` : "Sin datos aún"}
              </p>
            </div>
          </div>

          {compact && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>

        <p className="mt-4 text-sm text-navy-700">{analysis.summary}</p>

        {/* Score Breakdown - Only show if has data */}
        {hasData && analysis.breakdown && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            {analysis.breakdown.documentos && (
              <div className="flex items-center gap-1 px-2 py-1 bg-white/50 rounded">
                <span className="text-navy-500">Docs:</span>
                <span className="font-medium">{analysis.breakdown.documentos.score}/{analysis.breakdown.documentos.max}</span>
              </div>
            )}
            {analysis.breakdown.ds160 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-white/50 rounded">
                <span className="text-navy-500">DS-160:</span>
                <span className="font-medium">{analysis.breakdown.ds160.score}/{analysis.breakdown.ds160.max}</span>
              </div>
            )}
            {analysis.breakdown.perfil && (
              <div className="flex items-center gap-1 px-2 py-1 bg-white/50 rounded">
                <span className="text-navy-500">Perfil:</span>
                <span className="font-medium">{analysis.breakdown.perfil.score}/{analysis.breakdown.perfil.max}</span>
              </div>
            )}
            {analysis.breakdown.simulador && (
              <div className="flex items-center gap-1 px-2 py-1 bg-white/50 rounded">
                <span className="text-navy-500">Práctica:</span>
                <span className="font-medium">{analysis.breakdown.simulador.score}/{analysis.breakdown.simulador.max}</span>
              </div>
            )}
            {analysis.breakdown.viaje && analysis.breakdown.viaje.score > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100/50 rounded text-emerald-700">
                <TrendingUp className="w-3 h-3" />
                <span>+{analysis.breakdown.viaje.score} viaje</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {expanded && hasData && (
        <div className="p-6 space-y-6">
          {/* Progress Strengths */}
          {((analysis.strengths && analysis.strengths.length > 0) || (analysis.progress_strengths && analysis.progress_strengths.length > 0)) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
                <h4 className="font-semibold text-navy-900">Tu Progreso</h4>
                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                  {(analysis.strengths?.length || 0) + (analysis.progress_strengths?.length || 0)}
                </span>
              </div>
              <div className="space-y-2">
                {[...(analysis.progress_strengths || []), ...(analysis.strengths || [])].map((strength, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100"
                  >
                    <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-emerald-800 text-sm">{strength.factor}</span>
                        {strength.impact && <span className="text-xs font-bold text-emerald-600">+{strength.impact}</span>}
                      </div>
                      {strength.explanation && <p className="text-xs text-emerald-700 mt-1">{strength.explanation}</p>}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Weaknesses */}
          {analysis.weaknesses && analysis.weaknesses.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <h4 className="font-semibold text-navy-900">Áreas de Riesgo</h4>
                <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                  {analysis.weaknesses.length}
                </span>
              </div>
              <div className="space-y-2">
                {analysis.weaknesses.map((weakness, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      weakness.severity === "high" 
                        ? "bg-red-50 border-red-200" 
                        : "bg-amber-50 border-amber-200"
                    }`}
                  >
                    <TrendingDown className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      weakness.severity === "high" ? "text-red-500" : "text-amber-500"
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-sm ${
                          weakness.severity === "high" ? "text-red-800" : "text-amber-800"
                        }`}>{weakness.factor}</span>
                        <span className={`text-xs font-bold ${
                          weakness.severity === "high" ? "text-red-600" : "text-amber-600"
                        }`}>{weakness.impact}</span>
                      </div>
                      <p className={`text-xs mt-1 ${
                        weakness.severity === "high" ? "text-red-700" : "text-amber-700"
                      }`}>{weakness.explanation}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Risk Factors */}
          {analysis.risk_factors && analysis.risk_factors.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-amber-600" />
                </div>
                <h4 className="font-semibold text-navy-900">Factores a Considerar</h4>
              </div>
              <div className="space-y-2">
                {analysis.risk_factors.map((factor, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 bg-navy-50 rounded-lg border border-navy-100"
                  >
                    <Shield className="w-4 h-4 text-navy-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-navy-700 text-sm">{factor.factor}</span>
                        <span className="text-xs font-bold text-navy-500">{factor.impact}</span>
                      </div>
                      <p className="text-xs text-navy-600 mt-1">{factor.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations - Merge progress_recommendations */}
          {((analysis.recommendations && analysis.recommendations.length > 0) || (analysis.progress_recommendations && analysis.progress_recommendations.length > 0)) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-blue-600" />
                </div>
                <h4 className="font-semibold text-navy-900">Próximos Pasos</h4>
              </div>
              <div className="space-y-2">
                {[...(analysis.progress_recommendations || []), ...(analysis.recommendations || [])].map((rec, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100"
                  >
                    <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-700">{idx + 1}</span>
                    </div>
                    <p className="text-sm text-blue-800">{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default RiskAnalysisCard;
